import { ReactNode } from 'react';

/**
 * 카드 왼쪽 열 — **레일 + 체크박스 + 번호** (2026-09-01 QA35).
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 왜 컴포넌트로 뽑았나
 * ─────────────────────────────────────────────────────────────
 * 회원 카드(`BriefingCard`)와 세팅 과제 카드(`SetupTaskCard`)가 **같은 줄에
 * 이어지는 한 벌의 목록**인데, 왼쪽 열을 각자 그리고 있었다. QA29 에서 과제
 * 카드에 번호를 새로 달 때 회원 카드의 값을 손으로 옮겨 적었고, 그때는 같았다.
 *
 * 그런데 손으로 맞춘 것은 **갈라진다.** 실제로 두 가지가 어긋났다.
 *   ① 과제 카드에는 **좌측 레일(5px)이 없었다.** 회원 카드는 등급색 레일이
 *      먼저 서고 그다음 연한 열이 오는데, 과제 카드는 연한 열이 카드 모서리에
 *      바로 붙어서 둥근 모서리에 잘려 보였다.
 *   ② QA34 로 과제 카드가 짧아지자(설명문·대상 상자·버튼 제거) 왼쪽 열이
 *      본문보다 길어졌다. 열은 `align-stretch` 라 카드를 자기 높이까지 늘리고,
 *      **번호가 카드 아래 모서리에 딱 붙었다.** 회원 카드는 본문이 길어서
 *      이 상황이 안 나왔을 뿐이다.
 *
 * 그래서 **번호가 붙은 열은 여기 한 곳에서만 그린다.** 다음에 카드가 또
 * 짧아져도 두 카드가 같이 움직인다.
 *
 * ⚠️ 체크박스는 넘겨받는다(`children`). 두 카드가 **하는 일이 다르다** —
 *    회원 카드는 서버에 완료를 기록하고 목록 클릭 전파를 막아야 하며, 과제
 *    카드는 이 브라우저에만 남긴다. 생김새만 같을 뿐 동작이 달라서, 억지로
 *    합치면 한쪽 규칙이 다른 쪽으로 새어 나간다.
 */

/** 열 폭 — 두 카드가 같은 값을 써야 번호가 세로로 한 줄에 선다 */
export const CARD_INDEX_WIDTH = 66;

/**
 * 번호 아래 여백.
 *
 * 🔴 **0 으로 두지 마라.** 본문이 짧은 카드에서 번호가 카드 아래 모서리에
 *    그대로 붙는다(QA35 에서 실제로 그렇게 보였다). 카드 높이는 이 열과
 *    본문 중 **긴 쪽**이 정하므로, 여기 여백이 곧 마지막 안전선이다.
 */
const BOTTOM_PAD = 14;

type Props = {
    /** 좌측 레일 색 — 회원 카드는 등급색, 과제 카드는 파랑 */
    rail: string;
    /** 열 배경(연한 톤) */
    tint: string;
    /** 번호 글자색 */
    accent: string;
    /** 0-based. 화면에는 +1 해서 그린다 */
    index: number;
    /** 위 여백 — 회원 카드 상세는 24, 목록·과제는 15 */
    paddingTop?: number;
    /** 체크박스 (카드마다 동작이 다르다 — 파일 머리말 참고) */
    children: ReactNode;
};

const CardIndexColumn = ({ rail, tint, accent, index, paddingTop = 15, children }: Props) => (
    <>
        {/* 좌측 레일 — 회원 카드는 등급색이 여기서 읽힌다 */}
        <div style={{ width: 5, flex: 'none', background: rail }} />
        <div
            className="flex flex-none flex-col items-center"
            style={{ width: CARD_INDEX_WIDTH, paddingTop, paddingBottom: BOTTOM_PAD, background: tint }}
        >
            {children}
            {/* 숫자만 있으면 스크린리더가 맥락 없는 「3」을 읽는다 */}
            <span
                aria-label={`${index + 1}번째 카드`}
                style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: accent,
                    opacity: 0.5,
                    marginTop: 8,
                }}
            >
                {index + 1}
            </span>
        </div>
    </>
);

export default CardIndexColumn;
